import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPublic, type UserUpdate } from "shared/schemas";
import { ApiError, apiFetch } from "../lib/api-client.js";

export const ME_QUERY_KEY = ["user", "me"] as const;

/**
 * Fire-and-update PATCH to /users/me. Used by setting hooks (theme, language,
 * markerFormat) when the toggle fires from any UI surface and the user has a
 * live session — keeps the server copy in step with localStorage / URL.
 * Errors are swallowed: the local change has already happened; the next page
 * load reconciles via useUserSettingsSync.
 */
export async function patchUserSettings(qc: QueryClient, update: UserUpdate): Promise<void> {
  try {
    const next = await apiFetch<UserUpdate, UserPublic>(
      { method: "PATCH", path: "/api/v1/users/me", body: update },
      UserPublic,
    );
    qc.setQueryData(ME_QUERY_KEY, next);
  } catch {
    // intentionally silent — see jsdoc
  }
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<undefined, UserPublic>({ path: "/api/v1/users/me" }, UserPublic);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiFetch<undefined, void>({ method: "POST", path: "/api/v1/auth/logout" }, null);
    },
    onSuccess: () => {
      qc.setQueryData(ME_QUERY_KEY, null);
      qc.invalidateQueries();
    },
  });
}
